import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PenLine, BadgeCheck, Percent, Euro, Users2, Clock, Gauge, Coins,
  Sparkles, Target, CalendarClock, TrendingUp, Flag,
} from 'lucide-react';
import { useTenant } from '@/context/TenantContext';
import { useAuth } from '@/context/AuthContext';
import { useFilters } from '@/context/FilterContext';
import { usePost } from '@/lib/useData';
import { comparisonInfo } from '@/lib/periods';
import { PageHeader } from '@/components/common/PageHeader';
import { SectionCard } from '@/components/common/SectionCard';
import { KpiCard } from '@/components/kpi/KpiCard';
import { DonSegmentation } from '@/components/kpi/DonSegmentation';
import { SegmentTrend, SegmentAge } from '@/components/kpi/SegmentCharts';
import { CampaignCodeKpiTable } from '@/components/campaign/CampaignCodeKpiTable';
import { TrendComparisonNote } from '@/components/common/TrendComparisonNote';
import { GridSkeleton, CardSkeleton } from '@/components/common/Loading';
import { EmptyState } from '@/components/common/EmptyState';
import { TrendAreaChart } from '@/components/charts/TrendAreaChart';
import { HorizontalBars } from '@/components/charts/HorizontalBars';
import { AgeDonut } from '@/components/charts/AgeDonut';
import { ScoreGauge } from '@/components/common/ScoreGauge';
import { StatusPill } from '@/components/common/StatusPill';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { fmtInt, fmtPct, fmtEur, fmtNum } from '@/lib/format';

function tickDate(p) {
  if (typeof p === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(p)) {
    const d = new Date(p);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  }
  return p;
}

const spark = (series, key) => (series || []).map((s) => ({ x: s.period, y: s[key] }));

export default function Dashboard() {
  const navigate = useNavigate();
  const { association, isCampaignManager } = useTenant();
  const { isAdmin } = useAuth();
  const { body, clearAll, period, periodKey, comparePeriod, parrainageOn, parrainageSeuil } = useFilters();
  const { data, isLoading, isError } = usePost('/analytics/dashboard', body, true,
    { refetchInterval: 60_000, staleTime: 30_000 });

  const kpis = data?.kpis;
  const series = data?.series || [];
  const cmp = useMemo(() => comparisonInfo(periodKey, period, comparePeriod), [periodKey, period, comparePeriod]);

  const cards = useMemo(() => {
    if (!kpis) return [];
    return [
      { label: 'BS signés', value: fmtInt(kpis.bs_rue.value), icon: PenLine, trend: kpis.bs_rue.pct_change, spark: spark(series, 'bs_rue') },
      { label: 'BS validés', value: fmtInt(kpis.bs_reel.value), icon: BadgeCheck, trend: kpis.bs_reel.pct_change, spark: spark(series, 'bs_reel'), accent: true },
      { label: 'Taux de transformation', value: fmtPct(kpis.taux_transfo.value), icon: Percent, trend: kpis.taux_transfo.pct_change, spark: spark(series, 'taux_transfo') },
      { label: 'Don moyen', value: fmtEur(kpis.don_moyen.value), icon: Euro, trend: kpis.don_moyen.pct_change, spark: spark(series, 'don_moyen') },
      { label: 'Âge moyen', value: `${fmtNum(kpis.age_moyen.value, 1)} ans`, icon: Users2, trend: kpis.age_moyen.pct_change },
      { label: 'Heures réalisées', value: fmtInt(kpis.heures.value), icon: Clock, trend: kpis.heures.pct_change, spark: spark(series, 'heures') },
      { label: 'Productivité horaire', value: `${fmtNum(kpis.productivite.value, 2)} BS/h`, icon: Gauge, trend: kpis.productivite.pct_change, spark: spark(series, 'productivite') },
      { label: 'Montant collecté (mensuel)', value: fmtEur(kpis.montant.value, 0), icon: Coins, trend: kpis.montant.pct_change, spark: spark(series, 'montant') },
    ];
  }, [kpis, series]);

  const obj = data?.objective;
  const score = data?.score;

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Bonjour, ${association?.short_name || 'SHARITI'}`}
        subtitle={association?.tagline || 'Vue d’ensemble de vos performances Face-to-Face'}
        icon={TrendingUp}
        actions={
          isAdmin ? (
            <Button className="gap-1.5 rounded-xl text-white" style={{ background: 'var(--tenant-accent-500)' }}
              onClick={() => navigate('/analyses?summary=1')} data-testid="dashboard-resume-button">
              <Sparkles className="h-4 w-4" /> Résumé Direction
            </Button>
          ) : null
        }
      />

      {isError && <EmptyState title="Erreur de chargement" body="Impossible de récupérer les données pour le moment." />}

      {isLoading ? <GridSkeleton count={8} /> : (
        data && data.record_count === 0 ? (
          <EmptyState onClear={clearAll} />
        ) : (
          <>
            {/* KPI grid */}
            <div>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-sm font-semibold text-foreground">Indicateurs clés</h2>
                <TrendComparisonNote />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                {cards.map((c, i) => <KpiCard key={i} {...c} trendTooltip={cmp.full} />)}
              </div>
            </div>

            {/* Segmentation des dons (réguliers vs parrainage) — associations « parrainage » uniquement */}
            {association?.parrainage?.enabled && <DonSegmentation body={body} />}

            {/* Objective + Score */}
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
              <SectionCard title="Pilotage par objectif" subtitle={obj?.label} className="xl:col-span-2"
                action={obj && <StatusPill status={obj.status} />} testid="objective-card">
                {obj ? (
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div>
                      <div className="flex items-end justify-between">
                        <span className="font-display text-4xl font-semibold tabular">{fmtPct(obj.pct)}</span>
                        <span className="text-sm text-muted-foreground">{fmtInt(obj.achieved)} / {fmtInt(obj.target)} BS</span>
                      </div>
                      <Progress value={Math.min(obj.pct * 100, 100)} className="mt-3 h-2.5" />
                      <div className="mt-4 flex items-center gap-2 rounded-xl accent-bg-soft px-3 py-2 text-sm">
                        <Flag className="h-4 w-4 accent-text" />
                        <span className="font-medium">
                          {obj.status === 'atteint' ? 'Nous sommes dans les clous.' : obj.status === 'attention' ? 'Vigilance : rythme à maintenir.' : 'Retard à rattraper.'}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <Metric label="BS restants" value={fmtInt(obj.remaining)} icon={Target} />
                      <Metric label="Jours restants" value={fmtInt(obj.days_remaining)} icon={CalendarClock} />
                      <Metric label="Rythme requis / jour" value={fmtNum(obj.required_pace, 1)} icon={Gauge} />
                      <Metric label="Projection fin de période" value={fmtInt(obj.projection)} icon={TrendingUp} />
                    </div>
                  </div>
                ) : (
                  <p className="py-8 text-center text-sm text-muted-foreground">{isCampaignManager
                    ? "Aucun objectif personnel ne vous a encore été assigné."
                    : "Aucun objectif défini pour cette association."}</p>
                )}
              </SectionCard>

              <SectionCard title="Score de performance" subtitle="Sur 100, calculé à partir des KPI clés">
                {score ? (
                  <div className="flex flex-col items-center py-2">
                    <ScoreGauge score={score.score} rating={score.rating} />
                    <div className="mt-4 grid w-full grid-cols-2 gap-2 text-center">
                      {Object.entries(score.components || {}).map(([k, v]) => (
                        <div key={k} className="rounded-xl bg-muted/50 px-2 py-2">
                          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{labelComp(k)}</p>
                          <p className="font-display text-sm font-semibold tabular">{v}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : <div className="h-40" />}
              </SectionCard>
            </div>

            {/* Trend + breakdown */}
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
              <SectionCard title="Évolution des signatures"
                subtitle={parrainageOn ? 'BS validés dans le temps · par famille' : 'BS signés vs BS validés'}
                className="xl:col-span-2">
                {parrainageOn ? (
                  <SegmentTrend body={body} seuil={parrainageSeuil} />
                ) : series.length ? (
                  <TrendAreaChart data={series} xTickFormatter={tickDate}
                    formatTip={(v) => fmtInt(v)} formatY={(v) => fmtInt(v)}
                    series={[
                      { key: 'bs_rue', name: 'BS signés', color: '#5B6472' },
                      { key: 'bs_reel', name: 'BS validés', color: 'var(--tenant-accent-500)' },
                    ]} />
                ) : <EmptyState onClear={clearAll} />}
              </SectionCard>

              <SectionCard title="Répartition par âge" subtitle={parrainageOn ? 'Profil des donateurs · par famille' : 'Profil des donateurs'}>
                {parrainageOn ? (
                  <SegmentAge body={body} seuil={parrainageSeuil} />
                ) : data?.age_distribution?.some((a) => a.count > 0)
                  ? <AgeDonut data={data.age_distribution} />
                  : <EmptyState body="Aucune donnée d'âge sur la période." />}
              </SectionCard>
            </div>

            <SectionCard title="Meilleurs codes campagne" subtitle="Classés par BS validés"
              action={<Button variant="ghost" size="sm" onClick={() => navigate('/campagnes')} data-testid="dashboard-see-campaigns">Voir tout</Button>}>
              {data?.top_codes?.length ? (
                <HorizontalBars height={Math.max(220, data.top_codes.length * 60)}
                  yWidth={150}
                  data={data.top_codes.map((c) => ({ key: c.key, value: c.bs_reel }))}
                  subLabels={Object.fromEntries((data.top_codes || []).map((c) => [c.key, [c.canal, c.region].filter(Boolean)]))}
                  formatTip={(v) => `${fmtInt(v)} BS validés`} />
              ) : <EmptyState onClear={clearAll} />}
            </SectionCard>

            {/* Detailed per-campaign-code KPI table (age moyen & don moyen highlighted) */}
            {data?.code_breakdown?.length ? (
              <CampaignCodeKpiTable
                items={data.code_breakdown}
                title="Détail par code campagne"
                subtitle={`Âge moyen & don moyen — ${data.code_breakdown.length} codes sur la période`}
                testid="dashboard-code-kpi-table"
              />
            ) : null}
          </>
        )
      )}
    </div>
  );
}

const Metric = ({ label, value, icon: Icon }) => (
  <div className="rounded-xl border bg-card p-3">
    <div className="flex items-center gap-1.5 text-muted-foreground">
      {Icon && <Icon className="h-3.5 w-3.5" />}
      <span className="text-[11px]">{label}</span>
    </div>
    <p className="mt-1 font-display text-lg font-semibold tabular text-foreground">{value}</p>
  </div>
);

function labelComp(k) {
  return { transformation: 'Transfo', productivite: 'Productiv.', don_moyen: 'Don moyen', tendance: 'Tendance' }[k] || k;
}
